import {
  Dispatch,
  SetStateAction,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { TimeCardEntry } from '../../../../TimeCards/TimeCard.types.ts';
import { useMutation, useQuery } from '@tanstack/react-query';
import { DB, Schemas } from '@tonic/central-specialties-utils';

import {
  Center,
  Icons,
  Spinner,
  useAlert,
  useUser,
} from '@tonic/central-specialties-ui-themed';
import { Form, FormFieldsObject, FormProps } from '../../Form.tsx';
import { TimeframeData } from '../../FormField/specific';
import moment from 'moment-timezone';
import { useFormJobOptions } from '../../hooks/useFormJobOptions.ts';
import {
  FormControl,
  FormControlLabel,
  FormControlLabelText,
  Icon,
  Text,
  useToken,
} from '@gluestack-ui/themed';
import { useFormJobCodeOptions } from '../../hooks/useFormJobCodeOptions.ts';
import { hasDetails } from '../../../../../utils/errorGuards.ts';

interface TimeCardEntryFormProps {
  /**
   * If not provided, the form will be in create mode, otherwise it will be in edit mode
   */
  timeCardEntryId?: string;
  formProps?: Partial<FormProps>;
  targetDate?: Date;
  onSuccess?: (newEntryDate?: string) => void;
  isAdminSubmitting?: boolean;
  setNoWorkEntry?: Dispatch<SetStateAction<boolean>>;
  setNoWorkType?: Dispatch<SetStateAction<string>>;
  setNewEntryJob?: (jobId: string) => void;
  setNewEntryEmployee?: (employeeId: string) => void;
}

export const TimeCardEntryForm = ({
  timeCardEntryId,
  ...restProps
}: TimeCardEntryFormProps) => {
  const { alert } = useAlert();
  const {
    data: timeCardEntry,
    isPending,
    error,
  } = useQuery({
    queryKey: ['timeCardEntry', timeCardEntryId],
    queryFn: async () =>
      DB.GET('/time-card-entries/{id}', {
        params: { path: { id: timeCardEntryId } },
      }).then((res: { data: Schemas['TimeCardEntryModel'] }) => res.data),
    enabled: !!timeCardEntryId,
  });

  if (isPending && timeCardEntryId !== undefined) {
    return (
      <Center h="$full" minHeight="$96" flex={1}>
        <Spinner />
      </Center>
    );
  }

  if (error) {
    alert({
      message: 'There was an error fetching the time card entry.',
      status: 'error',
      timeout: 10000,
    });
    return (
      <Center w="$full" h="$full" gap="$6">
        <Icon as={Icons.AlertCircle} color="$error500" w="$16" h="$16" />
        <Text size="lg" color="$error500">
          There was an error retrieving time card entry {timeCardEntryId}
        </Text>
      </Center>
    );
  }
  return (
    (timeCardEntryId === undefined || !!timeCardEntry) && (
      <TimeCardEntryFormInner timeCard={timeCardEntry} {...restProps} />
    )
  );
};

type TimeCardEntryFormData = {
  employeeId: string;
  jobId?: string | null;
  laborCodeId?: string | null;
  divisionCodeId?: string | null;
  costCodeId?: string | null;
  equipmentNumber?: string | null;
  supervisorId?: string | null;
  startTime: string;
  endTime?: string | null;
  isRepair: boolean;
  isNoWorkEntry: boolean;
  noWorkType?: string | null;
  workDescription: string;
  hoursMiles?: string | null;
  updatedAt?: string | null;
};

interface TimeCardEntryFormInnerProps extends TimeCardEntryFormProps {
  /**
   * If not provided, the form will be in create mode, otherwise it will be in edit mode
   */
  timeCard?: TimeCardEntry;
}

export const TimeCardEntryFormInner = ({
  timeCard,
  formProps,
  targetDate,
  onSuccess,
  setNoWorkEntry,
  setNoWorkType,
  setNewEntryJob,
  setNewEntryEmployee,
}: TimeCardEntryFormInnerProps) => {
  const [mode, setMode] = useState<'create' | 'edit'>(
    timeCard && 'employee' in timeCard ? 'edit' : 'create',
  );
  const [defaults, setDefaults] = useState<TimeCardEntryFormData | null>(null);
  const { alert } = useAlert();
  const user = useUser();
  const [noWorkTypeState, setNoWorkTypeState] = useState<string>('');
  const [amendedDate, setAmendedDate] = useState<string | null>(() =>
    timeCard?.startTime
      ? moment
          .utc(timeCard.startTime)
          .tz('America/Chicago')
          .format('YYYY-MM-DD')
      : null,
  );
  const maxAmendDate = moment().endOf('week').format('YYYY-MM-DD');
  const grayBorder = useToken('colors', 'gray300');

  useEffect(() => {
    setMode(timeCard && 'employee' in timeCard ? 'edit' : 'create');
  }, [timeCard]);

  ///-----------------------------------------------------------------------
  // FORM OPTIONS DATA FETCHING & STATE
  ///-----------------------------------------------------------------------
  // EMPLOYEE OPTIONS
  const [employeeOptions, setEmployeeOptions] = useState<Record<
    string,
    string
  > | null>(null);

  // Populate employee options if we're in create mode
  useEffect(() => {
    // if this is a new timecard entry (we're in create mode), we to populate a list of employee id options
    if (mode === 'create') {
      if (!targetDate) {
        alert({
          status: 'error',
          message: 'No target date selected for new time card entry!',
          timeout: 10000,
        });
      }
      DB.GET('/users').then((res: { data: Schemas['UserModel'][] }) => {
        setEmployeeOptions(
          Object.fromEntries(
            res.data
              .filter((u: Schemas['UserModel']) => !u?.isDisabled)
              .map((u) => [u.fullName, u.id])
              .sort((a, b) => a[0].localeCompare(b[0])), // Sort by name,
          ),
        );
      });
    } else {
      setEmployeeOptions(null);
    }
  }, [alert, mode, targetDate]);

  // Job options
  const {
    allJobs,
    jobOptions,
    isPending: jobsPending,
    error: _jobsFetchError,
  } = useFormJobOptions();

  // Job code options
  const {
    laborCodeOptions,
    divisionCodeOptions,
    jobCodesDataGrouped,
    isPending: jobCodeOptionsPending,
    error: _jobCodesFetchError,
  } = useFormJobCodeOptions();
  const [selectedJobId, setSelectedJobId] = useState<string | null>(
    timeCard?.job?.id || null,
  );
  const [, setSelectedCostCodeId] = useState<string | null>(null);
  const [selectedDivisionCodeId, setSelectedDivisionCodeId] = useState<
    string | null
  >(timeCard?.divisionCode?.id || null);

  const costCodeOptions = useMemo<Record<string, string>>(() => {
    if (!jobCodesDataGrouped || !selectedDivisionCodeId) return {};
    const division = jobCodesDataGrouped.divisionCodes?.find(
      (d) => d.id === selectedDivisionCodeId,
    );
    if (!division) return {};
    return Object.fromEntries(
      division.costCodes?.map((cc) => [
        `${cc.code} - ${cc.description}`,
        cc.id,
      ]) ?? [],
    );
  }, [jobCodesDataGrouped, selectedDivisionCodeId]);
  const [selectedLaborCodeId, setSelectedLaborCodeId] = useState<string | null>(
    null,
  );
  const [enteredEquipmentNumber, setEnteredEquipmentNumber] = useState<
    string | null
  >('');
  const [equipmentEntityPending, setEquipmentEntityPending] = useState(false);
  const [enteredEquipmentEntity, setEnteredEquipmentEntity] = useState<
    Schemas['EquipmentItemResponse'] | null
  >(null);
  // Stash the last non-101 equipment number so it can be restored if the user
  // switches away from labor code 101 (which clears + disables the field).
  const stashedEquipmentNumberRef = useRef<string>('');

  useEffect(() => {
    if (selectedJobId !== timeCard?.job?.id) {
      setSelectedDivisionCodeId(null);
    }
  }, [selectedJobId, timeCard?.job?.id]);

  const {
    isPending: associatedJobCodesPending,
    error: associatedJobCodesFetchError,
  } = useQuery({
    queryKey: ['jobCodesByType', selectedJobId],
    queryFn: async () => {
      return await DB.GET('/jobs/{id}/job-codes', {
        params: { path: { id: selectedJobId } },
      }).then((res: { data: Schemas['JobCodesByTypeResponse'] }) => res.data);
    },
    enabled: !!selectedJobId,
  });

  // EQUIPMENT OPTIONS
  const {
    data: equipmentOptions,
    isPending: equipmentPending,
    error: equipmentFetchError,
  } = useQuery({
    queryKey: ['equipmentOptionsFor', selectedLaborCodeId],
    queryFn: () =>
      DB.GET('/equipment/labor-code/{laborCodeId}', {
        params: {
          path: {
            laborCodeId: selectedLaborCodeId,
          },
        },
      }).then(
        (res: { data: Schemas['EquipmentListResponse'] }) => res.data.equipment,
      ),
    enabled: !!selectedLaborCodeId,
  });

  const fetchEquipmentByNumber = useCallback(
    async (equipmentNumber: string) => {
      setEquipmentEntityPending(true);
      return await DB.GET('/equipment/by-number/{number}', {
        params: {
          path: { number: equipmentNumber },
        },
      }).then((res: { data: Schemas['EquipmentItemResponse'] }) => {
        setEquipmentEntityPending(false);
        return res?.data ? res.data : null;
      });
    },
    [],
  );

  useEffect(() => {
    if (enteredEquipmentNumber) {
      const matchingEquipment =
        Array.isArray(equipmentOptions) && equipmentOptions.length > 0
          ? equipmentOptions.find((eq) => eq.number === enteredEquipmentNumber)
          : undefined;

      if (matchingEquipment) {
        setEquipmentEntityPending(false);
        setEnteredEquipmentEntity(matchingEquipment);
      } else {
        setEquipmentEntityPending(true);
        fetchEquipmentByNumber(enteredEquipmentNumber).then(
          (res: Schemas['EquipmentItemResponse'] | null) => {
            setEquipmentEntityPending(false);
            setEnteredEquipmentEntity(res);
          },
        );
      }
    } else {
      setEquipmentEntityPending(false);
      setEnteredEquipmentEntity(null);
    }
  }, [equipmentOptions, enteredEquipmentNumber, fetchEquipmentByNumber]);

  useEffect(() => {
    if (associatedJobCodesFetchError) {
      alert({
        status: 'error',
        message:
          'There was an error fetching job codes associated with this job number.',
      });
    } else if (equipmentFetchError) {
      alert({
        status: 'error',
        message:
          'There was an error fetching equipment options associated with this labor code.',
      });
    }
  }, [alert, equipmentFetchError, associatedJobCodesFetchError]);

  // SUPERVISOR OPTIONS
  const { data: supervisorOptions, isPending: supervisorsPending } = useQuery({
    queryKey: ['supervisors'],
    queryFn: async () =>
      DB.GET('/users/{userId}/supervisors', {
        params: {
          path: { userId: mode === 'edit' ? timeCard?.employee?.id : user?.id },
        },
      }).then((res: { data: Schemas['UserModel'][] }) =>
        res.data?.filter((u) => !u?.isDisabled),
      ),
  });

  const joinTimeframeDataToDate = useCallback(
    ({ startTime, endTime }: TimeframeData): TimeframeData => {
      const baseDate = amendedDate
        ? moment.tz(amendedDate, 'YYYY-MM-DD', 'America/Chicago')
        : targetDate
          ? moment.tz(targetDate, 'America/Chicago')
          : moment.tz('America/Chicago');
      const dateString = baseDate.format('YYYY-MM-DD');

      // Combine the date and times with the Central Time Zone
      const startDateTime = moment
        .tz(`${dateString}T${startTime}`, 'America/Chicago')
        .toISOString();
      // For the end time, if it's "00:00", treat it as midnight on the following day.
      let endDateTime: string;
      if (endTime === '00:00') {
        const nextDateString = baseDate
          .clone()
          .add(1, 'day')
          .format('YYYY-MM-DD');
        endDateTime = moment
          .tz(`${nextDateString}T${endTime}`, 'America/Chicago')
          .toISOString();
      } else {
        endDateTime = moment
          .tz(`${dateString}T${endTime}`, 'America/Chicago')
          .toISOString();
      }
      return {
        startTime: startDateTime,
        endTime: endDateTime,
      };
    },
    [amendedDate, targetDate],
  );

  ///-----------------------------------------------------------------------
  // SUBMISSION HANDLERS
  ///-----------------------------------------------------------------------
  const { mutateAsync: updateTimeCardEntry } = useMutation({
    mutationFn: ({
      id,
      data,
    }: {
      id: string;
      data: Schemas['TimeCardEntryRequest'];
    }) =>
      DB.POST('/time-card-entries/{id}', {
        params: {
          path: {
            id,
          },
        },
        body: data,
      }),
  });

  const { mutateAsync: createTimeCardEntry } = useMutation({
    mutationFn: (data: Schemas['TimeCardEntryRequest']) =>
      DB.POST('/time-card-entries', {
        body: data,
      }),
  });

  const submit = useCallback(
    async (data: Record<string, any>) => {
      const {
        employeeId,
        jobId,
        laborCodeId,
        divisionCodeId,
        costCodeId,
        equipmentNumber,
        supervisorId,
        startTime: startTimeHoursMinutes,
        endTime: endTimeHoursMinutes,
        isRepair,
        isNoWorkEntry,
        noWorkType,
        workDescription,
        hoursMiles,
      } = data;

      const equipmentId = equipmentOptions?.find(
        (eq) => eq.number === equipmentNumber,
      )?.id;

      let { startTime, endTime } = joinTimeframeDataToDate({
        startTime: startTimeHoursMinutes,
        endTime: endTimeHoursMinutes,
      });

      // Convert back to UTC before sending
      startTime = moment(startTime).tz('America/Chicago').utc().toISOString();
      endTime = moment(endTime).tz('America/Chicago').utc().toISOString();

      // Create the actual payload, nullifying irrelevant fields for no-work entries
      const payload: Schemas['TimeCardEntryRequest'] = {
        employeeId,
        jobId: isNoWorkEntry ? null : jobId,
        laborCodeId: isNoWorkEntry ? null : laborCodeId,
        divisionCodeId: isNoWorkEntry ? null : divisionCodeId,
        costCodeId: isNoWorkEntry ? null : costCodeId,
        equipmentId: isNoWorkEntry ? null : equipmentId ?? null,
        supervisorId:
          isNoWorkEntry && !['PTO', 'ESST'].includes(noWorkType)
            ? null
            : supervisorId,
        startTime,
        endTime: isNoWorkEntry ? null : endTime,
        isRepair: isNoWorkEntry ? false : isRepair,
        isNoWorkEntry,
        noWorkType: isNoWorkEntry ? noWorkType : null,
        workDescription:
          data?.isNoWorkEntry === true &&
          !['PTO', 'ESST'].includes(data?.noWorkType)
            ? data.noWorkType
            : workDescription,
        hoursMiles: isNoWorkEntry ? null : hoursMiles || null,
      };

      const handleError = (
        e: unknown,
        fallbackAlertMessage: string,
      ): Record<string, string> | void => {
        alert({
          status: 'error',
          message: fallbackAlertMessage,
          timeout: 10000,
        });
        if (hasDetails(e) && e.details.length > 0) {
          const errors: Record<string, string> = {};
          for (const eMessage of e.details) {
            if (eMessage.toLowerCase().includes('requires equipment')) {
              errors.equipmentNumber = eMessage;
            } else if (eMessage.toLowerCase().includes('overlapping time')) {
              errors.duration = eMessage;
            } else if (
              eMessage.toLowerCase().includes('hours/miles is required')
            ) {
              errors.hoursMiles = eMessage;
            }
          }
          if (Object.keys(errors).length === 0) {
            errors.SUBMIT = e.details[0];
          }
          return errors;
        }
      };

      switch (mode) {
        case 'create':
          try {
            // Certain api errors don't throw automatically
            const { error } = await createTimeCardEntry(payload);
            if (error) {
              console.error(error);
              throw error;
            }
            alert({
              status: 'success',
              message: 'Time card entry created successfully',
              timeout: 5000,
            });
            onSuccess && onSuccess();
            return { success: 'Time card creation successful' };
          } catch (e) {
            console.error(e);
            const errorRes = handleError(
              e,
              'There was an error creating this time card entry.',
            );
            return { error: errorRes };
          }
          break;
        case 'edit':
          try {
            // Certain api errors don't throw automatically
            const { error } = await updateTimeCardEntry({
              id: timeCard!.id,
              data: payload,
            });
            if (error) {
              console.error(error);
              throw error;
            }
            alert({
              status: 'success',
              message: 'Time card entry updated successfully',
              timeout: 5000,
            });
            setDefaults({
              ...payload,
              updatedAt: new Date().toISOString(),
            });
            const originalDateString = targetDate
              ? moment.tz(targetDate, 'America/Chicago').format('YYYY-MM-DD')
              : null;
            const dateChanged =
              !!amendedDate &&
              !!originalDateString &&
              amendedDate !== originalDateString;
            onSuccess && onSuccess(dateChanged ? amendedDate : undefined);
            return { success: 'Time card update successful' };
          } catch (e) {
            console.error(e);
            const errorRes = handleError(
              e,
              'There was an error updating this time card entry.',
            );
            return { error: errorRes };
          }
          break;
        default:
          console.error('Invalid mode set in TimeCardEntryForm: ', mode);
          return { error: `Invalid mode set in TimeCardEntryForm: ${mode}` };
          break;
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      mode,
      updateTimeCardEntry,
      createTimeCardEntry,
      defaults,
      alert,
      onSuccess,
      timeCard?.id,
      equipmentOptions,
      setDefaults,
      joinTimeframeDataToDate,
      amendedDate,
      targetDate,
    ],
  );

  ///-----------------------------------------------------------------------
  /// SETTING DEFAULTS
  ///-----------------------------------------------------------------------
  const formatTimestampForTimeInput = (timestamp: string): string => {
    return timestamp
      ? moment.utc(timestamp).tz('America/Chicago').format('HH:mm')
      : '00:00';
  };

  useEffect(() => {
    setDefaults({
      employeeId: timeCard?.employee?.id || '',
      jobId: timeCard?.job?.id || '',
      laborCodeId: timeCard?.laborCode?.id || '',
      divisionCodeId: timeCard?.divisionCode?.id || '',
      costCodeId: timeCard?.costCode?.id || '',
      equipmentNumber: timeCard?.equipmentNumber || '',
      supervisorId: timeCard?.supervisor?.id,
      startTime: formatTimestampForTimeInput(timeCard?.startTime ?? ''),
      endTime: formatTimestampForTimeInput(timeCard?.endTime ?? ''),
      isNoWorkEntry: timeCard?.isNoWorkEntry || false,
      isRepair: timeCard?.isRepair || false,
      noWorkType: timeCard?.noWorkType || '',
      workDescription: timeCard?.workDescription || '',
      hoursMiles: timeCard?.hoursMiles || '',
      updatedAt: timeCard?.updatedAt || '',
    });
    setSelectedCostCodeId(timeCard?.costCode?.id ?? null);
    setSelectedDivisionCodeId(timeCard?.divisionCode?.id ?? null);
    setSelectedLaborCodeId(timeCard?.laborCode?.id ?? null);
    setEnteredEquipmentNumber(timeCard?.equipmentNumber || '');
    if (timeCard?.startTime) {
      setAmendedDate(
        moment
          .utc(timeCard.startTime)
          .tz('America/Chicago')
          .format('YYYY-MM-DD'),
      );
    }
  }, [timeCard, timeCard?.updatedAt]);

  const isLaborCode101 = useMemo(
    () =>
      !!selectedLaborCodeId &&
      Object.entries(laborCodeOptions || {}).some(
        ([label, id]) =>
          id === selectedLaborCodeId && label.startsWith('101 - '),
      ),
    [laborCodeOptions, selectedLaborCodeId],
  );

  const isDivisionCodeRP = useMemo(
    () =>
      jobCodesDataGrouped?.divisionCodes?.some(
        (dc) => dc.id === selectedDivisionCodeId && dc.code === 'RP',
      ) ?? false,
    [jobCodesDataGrouped, selectedDivisionCodeId],
  );

  const formFields: FormFieldsObject = useMemo(
    () => ({
      duration: {
        type: 'Timeframe',
        dataFieldKey: ['startTime', 'endTime'],
        label: 'Duration',
        required: true,
        // No-work checkbox in top right
        labelRightElement: {
          type: 'Checkbox',
          dataFieldKey: 'isNoWorkEntry',
          label: 'No work',
          size: 'lg',
          // nullify noWorkType when noWorkEntry is unchecked
          onChange: (val: boolean, data, setData) => {
            if (!val) {
              setData({
                ...data,
                noWorkType: '',
              });
              setNoWorkTypeState('');
              setNoWorkEntry && setNoWorkEntry(false);
              setNoWorkType && setNoWorkType('');
            }
          },
        },
        timezone:
          mode === 'create' && moment().tz(moment.tz.guess()).format('z'),
        // The input only manages time, but the API expects a full date-time string
        // preSubmitTransform: joinTimeframeDataToDate,
        validator: (timeframeData: TimeframeData) => {
          const { startTime, endTime } = joinTimeframeDataToDate(timeframeData);
          const start = moment(startTime);
          const end = moment(endTime);
          // handle startTime is less than endTime
          if (start.isAfter(end)) {
            return 'Start time must be before end time';
          }
          // handle 0 duration, excepting PTO and ESST no work days
          if (start.isSame(end) && !['PTO', 'ESST'].includes(noWorkTypeState)) {
            return 'Start and end times cannot be the same';
          }
          // ensure both start and end use 15 minute increments
          if (
            start.minute() % 15 !== 0 ||
            end.minute() % 15 !== 0 // ||
            // start.second() !== 0 ||
            // end.second() !== 0
          ) {
            return 'Start and end times must be in 15 minute increments';
          }
        },
        disabled: (data: typeof defaults) => {
          return data?.isNoWorkEntry;
        },
      },

      noWorkType: {
        type: 'Select',
        dataFieldKey: 'noWorkType',
        label: 'No work type',
        placeholder: 'Select a no-work type',
        options: {
          'PTO: request in Bamboo': 'PTO',
          Holiday: 'Holiday',
          'ESST: request in Bamboo': 'ESST',
          'Rain Day: request in Bamboo if you wish to use ESST': 'RainDay',
          'Day Off': 'DayOff',
        },
        onChange: (item) => {
          setNoWorkTypeState(item);
          setNoWorkEntry && setNoWorkEntry(true);
          setNoWorkType && setNoWorkType(item);
        },
        hidden: (formData: Record<string, any>) => {
          return !formData.isNoWorkEntry;
        },
        required: true,
      },

      employeeId: {
        type: 'Select',
        dataFieldKey: 'employeeId',
        label: 'Employee',
        options:
          mode === 'edit' ? [timeCard?.employee?.id] : employeeOptions ?? {},
        required: true,
        hidden: () => mode === 'edit',
        onChange: (val: string) => {
          setNewEntryEmployee && setNewEntryEmployee(val);
        },
      },

      jobNumber: {
        type: 'Select',
        dataFieldKey: 'jobId',
        label: 'Job number',
        placeholder: 'Select job number',
        options: jobOptions,
        required: true,
        hidden: (data: typeof defaults) => data?.isNoWorkEntry === true,
        onChange: (val: string) => {
          setSelectedJobId(val);
          setNewEntryJob && setNewEntryJob(val);
        },
      },

      jobName: {
        type: 'Text',
        dataFieldKey: 'jobName',
        label: 'Job name',
        readonly: true,
        populate: (data: Record<string, any>) => {
          if (!allJobs) {
            return 'No description';
          }
          return (
            allJobs.find((j: Schemas['JobCodeModel']) => j.id === data.jobId)
              ?.description || 'No description'
          );
        },
        hidden: (data: typeof defaults) => data?.isNoWorkEntry === true,
      },

      laborCodeId: {
        type: 'Select',
        dataFieldKey: 'laborCodeId',
        label: 'Labor code',
        placeholder: 'Select labor code',
        options: laborCodeOptions || {},
        hidden: (data: typeof defaults) => data?.isNoWorkEntry === true,
        validator: (val: string) => {
          if (!Object.values(laborCodeOptions || {}).includes(val)) {
            return 'Invalid labor code for the selected Job Number';
          }
          return null;
        },
        required: true,
        disabled: !selectedJobId,
        onChange: (
          val: string,
          newData: Record<string, any>,
          setFormData: (data: Record<string, any>) => void,
        ) => {
          setSelectedLaborCodeId(val);
          const selectedLabel = Object.entries(laborCodeOptions || {}).find(
            ([, id]) => id === val,
          )?.[0];
          const willBe101 = !!selectedLabel?.startsWith('101 - ');
          // Entering 101: stash the current equipment number and clear it.
          // Also clear isRepair — repair requires equipment, which is prohibited with 101.
          if (willBe101 && !isLaborCode101) {
            stashedEquipmentNumberRef.current =
              (newData?.equipmentNumber as string) || '';
            setEnteredEquipmentNumber('');
            setFormData({ ...newData, equipmentNumber: '', isRepair: false });
          }
          // Leaving 101: restore the previously entered equipment number.
          else if (!willBe101 && isLaborCode101) {
            const restored = stashedEquipmentNumberRef.current;
            setEnteredEquipmentNumber(restored);
            setFormData({ ...newData, equipmentNumber: restored });
            stashedEquipmentNumberRef.current = '';
          }
        },
        pending: !!selectedJobId && associatedJobCodesPending,
      },

      isRepair: {
        type: 'Radio',
        options: {
          Yes: true,
          No: false,
        },
        dataFieldKey: 'isRepair',
        label: 'Repair?',
        disabled: isLaborCode101 || !isDivisionCodeRP,
        hidden: (data: typeof defaults) => data?.isNoWorkEntry === true,
      },

      divisionCodeId: {
        type: 'Select',
        dataFieldKey: 'divisionCodeId',
        label: 'Division code',
        placeholder: 'Select division code',
        options: divisionCodeOptions || {},
        onChange: (
          item: string,
          newData: Record<string, any>,
          setFormData: (data: Record<string, any>) => void,
        ) => {
          setSelectedDivisionCodeId(item);
          const willBeRP =
            jobCodesDataGrouped?.divisionCodes?.some(
              (dc) => dc.id === item && dc.code === 'RP',
            ) ?? false;
          // Leaving RP: Repair can only be checked while RP is selected — clear it.
          if (!willBeRP && isDivisionCodeRP) {
            setFormData({ ...newData, isRepair: false });
          }
        },
        hidden: (data: typeof defaults) => data?.isNoWorkEntry === true,
        required: true,
        validator: (val: string) => {
          if (!Object.values(divisionCodeOptions || {}).includes(val)) {
            return 'Invalid division code for the selected Job Number';
          }
          return null;
        },
        disabled: !selectedJobId,
        pending: !!selectedJobId && associatedJobCodesPending,
      },

      costCodeId: {
        type: 'Select',
        dataFieldKey: 'costCodeId',
        label: 'Cost code',
        placeholder: 'Select cost code',
        options: costCodeOptions || {},
        hidden: (data: typeof defaults) => data?.isNoWorkEntry === true,
        required: true,
        validator: (val: string) => {
          if (!Object.values(costCodeOptions || {}).includes(val)) {
            return 'Invalid cost code for the selected Division Code';
          }
          return null;
        },
        disabled: !selectedDivisionCodeId,
        pending: !!selectedDivisionCodeId && associatedJobCodesPending,
      },

      equipmentNumber: {
        type: 'Text',
        dataFieldKey: 'equipmentNumber',
        label: 'Equipment number',
        placeholder: 'Enter equipment number',
        disabled: isLaborCode101,
        helperText: isLaborCode101
          ? 'Equipment cannot be added when labor code 101 is selected.'
          : undefined,
        hidden: (data: typeof defaults) => data?.isNoWorkEntry === true,
        onChange: setEnteredEquipmentNumber,
        required: isDivisionCodeRP,
      },

      equipmentDescription: {
        type: 'Text',
        dataFieldKey: 'equipmentDescription',
        label: 'Equipment description',
        populate: () => enteredEquipmentEntity?.description || 'No description',
        hidden: (data: typeof defaults) => data?.isNoWorkEntry === true,
        readonly: true,
        pending:
          (selectedLaborCodeId && equipmentPending) || equipmentEntityPending,
      },

      equipmentCategory: {
        type: 'Text',
        dataFieldKey: 'equipmentCategory',
        label: 'Equipment category',
        populate: () => enteredEquipmentEntity?.categoryName ?? '',
        hidden: (data: typeof defaults) =>
          data?.isNoWorkEntry === true || !enteredEquipmentEntity,
        readonly: true,
        pending:
          (selectedLaborCodeId && equipmentPending) || equipmentEntityPending,
      },

      hoursMiles: {
        type: 'Numeric',
        dataFieldKey: 'hoursMiles',
        label: 'Hours/Miles',
        placeholder: 'Hours/Miles',
        required: !!enteredEquipmentEntity,
        hidden: (data: typeof defaults) =>
          data?.isNoWorkEntry === true ||
          (!enteredEquipmentEntity && (mode !== 'edit' || !data?.hoursMiles)),
      },

      supervisorId: (() => {
        const activeSupervisors = supervisorOptions || [];
        const prePopulated = timeCard?.supervisor;
        const inactiveSupervisor =
          prePopulated &&
          !activeSupervisors.some(
            (u: Schemas['UserModel']) => u.id === prePopulated.id,
          )
            ? prePopulated
            : null;
        return {
          type: 'Select',
          dataFieldKey: 'supervisorId',
          label: 'Supervisor',
          placeholder: 'Select a supervisor',
          options: Object.fromEntries([
            ...(inactiveSupervisor
              ? [
                  [
                    `${inactiveSupervisor.fullName} (inactive)`,
                    inactiveSupervisor.id,
                  ],
                ]
              : []),
            ...activeSupervisors.map((opt: Schemas['UserModel']) => [
              opt.fullName,
              opt.id,
            ]),
          ]),
          required: true,
          validator: (val: string) => {
            if (inactiveSupervisor && val === inactiveSupervisor.id) {
              return 'This supervisor is no longer active. Please select another.';
            }
            return null;
          },
          hidden: (data: typeof defaults) =>
            data?.isNoWorkEntry === true &&
            !['PTO', 'ESST'].includes(data?.noWorkType),
        };
      })(),

      workDescription: {
        type: 'Text',
        dataFieldKey: 'workDescription',
        label: 'Work description',
        multiline: true,
        placeholder: 'Describe the work completed',
        hidden: (data: typeof defaults) =>
          data?.isNoWorkEntry === true &&
          !['PTO', 'ESST'].includes(data?.noWorkType),
        required: true,
      },
    }),
    [
      mode,
      timeCard?.employee?.id,
      timeCard?.supervisor?.id,
      employeeOptions,
      jobOptions,
      laborCodeOptions,
      selectedJobId,
      associatedJobCodesPending,
      divisionCodeOptions,
      jobCodesDataGrouped,
      costCodeOptions,
      selectedDivisionCodeId,
      selectedLaborCodeId,
      equipmentPending,
      equipmentEntityPending,
      supervisorOptions,
      setNoWorkEntry,
      joinTimeframeDataToDate,
      noWorkTypeState,
      setNewEntryEmployee,
      setNewEntryJob,
      allJobs,
      enteredEquipmentEntity,
      isLaborCode101,
      isDivisionCodeRP,
    ],
  );

  // Client-side backstop mirroring the mobile form: labor code 101 cannot
  // coexist with equipment. The disabled + auto-clear UX normally prevents this
  // state, so valid entries are unaffected; this only blocks the bad combination.
  const checkCanSubmit = useCallback(
    (data: typeof defaults) => {
      if (!data?.isNoWorkEntry && isLaborCode101 && !!enteredEquipmentEntity) {
        return false;
      }
      return true;
    },
    [isLaborCode101, enteredEquipmentEntity],
  );

  if (
    jobsPending ||
    jobCodeOptionsPending ||
    supervisorsPending ||
    !defaults ||
    !formFields ||
    (mode === 'create' && !employeeOptions)
  ) {
    return (
      <Center h="$full" minHeight="$96" flex={1}>
        <Spinner />
      </Center>
    );
  }

  return (
    <>
      {mode === 'edit' && (
        <FormControl mb="$4">
          <FormControlLabel>
            <FormControlLabelText>Entry Date</FormControlLabelText>
          </FormControlLabel>
          <input
            type="date"
            value={amendedDate ?? ''}
            max={maxAmendDate}
            onChange={(e) => setAmendedDate(e.target.value)}
            style={{
              padding: 8,
              border: `1px solid ${grayBorder}`,
              borderRadius: 3,
              outline: 'none',
            }}
          />
        </FormControl>
      )}
      <Form
        key={
          timeCard?.updatedAt ? `${timeCard?.id}-${timeCard?.updatedAt}` : 'new'
        }
        formData={defaults}
        fields={formFields}
        onSubmit={submit}
        checkCanSubmit={checkCanSubmit}
        {...formProps}
      />
    </>
  );
};
